// Landing page targeting "wallet for college students" searches.
import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import {
  SITE_URL,
  makeBreadcrumbJsonLd,
  makeFaqJsonLd,
} from "../seo/structuredData";
import idPhoto from "../assets/newest-version/ID Card Holder.jpg";
import keysPhoto from "../assets/newest-version/Keys Holder (1).jpg";
import trackerPhoto from "../assets/newest-version/Integrated Tracking Card.jpg";
import heroPhoto from "../assets/newest-version/Full Front - Everything extended.jpg";

const futura = "'Futura', sans-serif";

const COLLEGE_FAQ = [
  {
    q: "Why is The Brik a good wallet for college students?",
    a: "It keeps the three things students lose most, the student ID, dorm keys, and wallet, together in one trackable metal wallet. The ID sits in a quick-access front slot for swiping into dorms and dining halls, keys attach to the integrated keyring, and a rechargeable tracking card lets you find it from your phone.",
  },
  {
    q: "Can I swipe my student ID without taking it out?",
    a: "The ID sits in the front slot and slides out in one motion. Tap-based student IDs can often be read without removing the card, since the front slot is intentionally not RFID-blocking.",
  },
  {
    q: "Will my dorm key fit on the wallet?",
    a: "Yes. Every Brik includes a removable keyring that holds dorm keys, mailbox keys, and car keys. The keyring can be swapped for any keyring you already use.",
  },
  {
    q: "What happens if I lose the wallet?",
    a: "Every Brik ships with a rechargeable tracking card built in. Open the tracking app on your phone to see the wallet's location or make it ring. The battery lasts up to 6 months per charge.",
  },
  {
    q: "How much does it cost and how fast does it ship?",
    a: "The Metal Brik is $69.99. Orders are processed in about 1 business day and arrive within 5-7 days.",
  },
];

// Real metal-Brik buyer reviews (full set of 20 on /metal-wallet).
const STUDENT_REVIEWS = [
  {
    text: "I got it for college because I kept misplacing my dorm keys. This would have saved me a lot of stress freshman year.",
    label: "Verified buyer",
  },
  {
    text: "It's compact without forcing me to give up cash, keys, or the cards I actually use.",
    label: "Verified buyer",
  },
  {
    text: "It took me a day to get used to, but now I don't leave the house without it.",
    label: "Verified buyer",
  },
];

const sectionHeading = {
  fontFamily: futura,
  fontSize: "clamp(26px, 3vw, 36px)",
  margin: "0 0 12px",
  letterSpacing: "-0.5px",
  color: "#111",
};

const bodyText = {
  fontFamily: futura,
  fontSize: "16px",
  lineHeight: 1.65,
  color: "#444",
  margin: "0 0 16px",
};

const ctaButton = {
  display: "inline-block",
  padding: "16px 32px",
  background: "#ff7d1a",
  color: "white",
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: "15px",
  letterSpacing: "1px",
  fontFamily: futura,
  textTransform: "uppercase",
  textDecoration: "none",
  boxShadow: "0 6px 20px rgba(255,125,26,0.35)",
};

const videoJsonLd = {
  "@context": "https://schema.org",
  "@type": "VideoObject",
  name: "College Students Need This Wallet",
  description:
    "Founder Asray Gopa shows why the Brik was built for college: student ID up front, dorm keys attached, cards and cash inside, and a tracking card so your phone can find it.",
  thumbnailUrl: `${SITE_URL}/videos/college-students-need-this-wallet-poster.jpg`,
  contentUrl: `${SITE_URL}/videos/college-students-need-this-wallet.mp4`,
  uploadDate: "2026-07-16T12:00:00-05:00",
  duration: "PT34S",
};

const CollegeWallet = () => {
  const faqJsonLd = makeFaqJsonLd(COLLEGE_FAQ);
  const breadcrumbJsonLd = makeBreadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "Wallet for College Students", path: "/wallet-for-college-students" },
  ]);

  return (
    <div style={{ background: "white", minHeight: "100vh" }}>
      <SEO
        title="Wallet for College Students: ID, Keys & Tracking"
        description="The wallet built for college students who lose their ID and dorm keys. ID up front, keys attached, and a tracking card to find it from your phone. $69.99."
        path="/wallet-for-college-students"
        keywords="wallet for college students, college wallet, student id wallet, dorm key wallet, wallet that holds keys, keychain wallet for students, best wallet for students"
        jsonLd={[faqJsonLd, breadcrumbJsonLd, videoJsonLd]}
      />
      <Header />

      {/* Hero */}
      <section
        style={{
          background: "radial-gradient(ellipse at top, #2a2a2a 0%, #0c0c0c 70%)",
          color: "white",
          padding: "70px 24px 80px",
        }}
      >
        <div style={{ maxWidth: "860px", margin: "0 auto", textAlign: "center" }}>
          <h1
            style={{
              fontFamily: futura,
              fontSize: "clamp(30px, 4vw, 50px)",
              lineHeight: 1.12,
              letterSpacing: "-1px",
              margin: "0 0 18px",
            }}
          >
            The Wallet Built for College Students Who Keep Losing Their ID and
            Dorm Keys
          </h1>
          <p
            style={{
              fontFamily: futura,
              fontSize: "18px",
              lineHeight: 1.55,
              color: "#cfcfcf",
              maxWidth: "620px",
              margin: "0 auto 26px",
            }}
          >
            The Brik keeps the three things students lose most, the student
            ID, dorm keys, and wallet, together in one trackable metal wallet.
            Your ID sits in a quick-access front slot for swiping into dorms
            and dining halls, keys attach to the integrated keyring, and a
            rechargeable tracking card lets you find it all from your phone.
            $69.99.
          </p>
          <a href="/metal-wallet" style={ctaButton}>
            Get the Metal Brik · $69.99
          </a>
          <p
            style={{
              fontFamily: futura,
              fontSize: "13px",
              color: "#9be8b4",
              margin: "18px 0 0",
              fontWeight: 600,
            }}
          >
            ★★★★★ Rated 4.8/5 by metal Brik buyers · in stock, arrives
            in 5-7 days
          </p>
          <div style={{ maxWidth: "560px", margin: "36px auto 0" }}>
            <img
              fetchPriority="high"
              src={heroPhoto}
              alt="The Metal Brik wallet for college students with student ID, cards, cash, and dorm keys attached"
              style={{ width: "100%", borderRadius: "16px", display: "block" }}
            />
          </div>
        </div>
      </section>

      {/* The problem */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={sectionHeading}>
            College runs on two things you keep losing
          </h2>
          <p style={bodyText}>
            Your student ID gets you into your dorm, the dining hall, the gym,
            and the library. Your dorm key gets you into your room. Lose either
            one and you're locked out, waiting on a replacement, and often
            paying a lock-change fee that can run well over a hundred dollars.
          </p>
          <p style={bodyText}>
            Most students carry the ID loose in a phone case, the keys on a
            bare lanyard, and a wallet somewhere in a backpack. Three separate
            things to lose. The Brik's answer is simple: make them one thing,
            and make that one thing findable from your phone.
          </p>
          <p style={{ ...bodyText, fontSize: "14px", color: "#666", margin: 0 }}>
            Parent shopping for your student? See the{" "}
            <a
              href="/gifts-for-college-students"
              style={{ color: "#ff7d1a", fontWeight: 600 }}
            >
              college gift guide for parents
            </a>
            .
          </p>
        </div>
      </section>

      {/* Founder story */}
      <section style={{ background: "#f7f7f7", padding: "70px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={sectionHeading}>It started with a $160 dorm key</h2>
          <p style={bodyText}>
            The Brik was started by Asray Gopa at Iowa State University after
            he lost his dorm key and got hit with a $160 replacement charge.
            The first prototype was cut from cardboard in his dorm room: one
            place for the ID, the cards, the cash, and the key that had just
            cost him $160.
          </p>
          <p style={bodyText}>
            Four years and 900+ original Briks later, the same layout is
            machined from solid anodized aluminum, with a rechargeable tracking
            card added so the wallet itself can never quietly disappear.
          </p>
          <a
            href="/about"
            style={{
              fontFamily: futura,
              color: "#ff7d1a",
              fontWeight: 700,
              fontSize: "15px",
              textDecoration: "none",
            }}
          >
            Read the full story →
          </a>
        </div>
      </section>

      {/* Video */}
      <section style={{ background: "white", padding: "70px 24px 30px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center" }}>
            The 30-second version
          </h2>
          <p style={{ ...bodyText, maxWidth: "520px", margin: "0 auto 26px" }}>
            Watch the founder walk through the exact carry: ID, dorm keys,
            cards, cash, and the tracking card.
          </p>
          <video
            controls
            playsInline
            preload="none"
            poster="/videos/college-students-need-this-wallet-poster.jpg"
            width={720}
            height={1280}
            style={{
              width: "100%",
              maxWidth: "340px",
              height: "auto",
              borderRadius: "16px",
              display: "block",
              margin: "0 auto",
              background: "#0c0c0c",
            }}
          >
            <source
              src="/videos/college-students-need-this-wallet.mp4"
              type="video/mp4"
            />
            Your browser doesn't support embedded video. Watch it on our
            Instagram instead.
          </video>
        </div>
      </section>

      {/* Feature mapping */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "40px" }}>
            Built around how students actually carry
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "28px",
            }}
          >
            {[
              {
                img: idPhoto,
                alt: "Student ID in the front slot of the Metal Brik wallet",
                title: "Your ID, always up front",
                text: "The front slot holds your student ID facing out, so swiping into the dorm or dining hall is one motion. No digging, no unzipping, no phone-case gamble.",
              },
              {
                img: keysPhoto,
                alt: "Dorm keys attached to the integrated key holder on the Metal Brik wallet",
                title: "Dorm keys, attached",
                text: "The integrated keyring holds your dorm key, mailbox key, and car key. Grab your wallet and your keys come with it. Lose-one-lose-both becomes find-one-find-both.",
              },
              {
                img: trackerPhoto,
                alt: "Rechargeable tracking card sliding into the Metal Brik wallet",
                title: "Findable from your phone",
                text: "A rechargeable tracking card is built into the wallet. Left it at the library? Open the app, see where it is, or make it ring. The battery lasts up to 6 months per charge.",
                link: { href: "/wallet-with-built-in-tracker", label: "How the built-in tracker works →" },
              },
            ].map((f) => (
              <div key={f.title}>
                <div
                  style={{
                    background: "#989898",
                    borderRadius: "14px",
                    overflow: "hidden",
                    aspectRatio: "4 / 3",
                    marginBottom: "16px",
                  }}
                >
                  <img
                    src={f.img}
                    alt={f.alt}
                    loading="lazy"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>
                <h3
                  style={{
                    fontFamily: futura,
                    fontSize: "19px",
                    margin: "0 0 8px",
                    color: "#111",
                    letterSpacing: "-0.3px",
                  }}
                >
                  {f.title}
                </h3>
                <p style={{ ...bodyText, fontSize: "15px", margin: 0 }}>{f.text}</p>
                {f.link && (
                  <a
                    href={f.link.href}
                    style={{
                      fontFamily: futura,
                      color: "#ff7d1a",
                      fontWeight: 700,
                      fontSize: "14px",
                      textDecoration: "none",
                      display: "inline-block",
                      marginTop: "10px",
                    }}
                  >
                    {f.link.label}
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Student reviews */}
      <section style={{ background: "#f7f7f7", padding: "70px 24px" }}>
        <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "36px" }}>
            What students say
          </h2>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: "20px",
            }}
          >
            {STUDENT_REVIEWS.map((r) => (
              <div
                key={r.name}
                style={{
                  background: "white",
                  borderRadius: "12px",
                  padding: "24px",
                  flex: "1 1 260px",
                  maxWidth: "320px",
                  border: "1px solid #e6e6e6",
                }}
              >
                <div
                  style={{ color: "#f5b315", fontSize: "16px", letterSpacing: "2px", marginBottom: "10px" }}
                  aria-label="5 out of 5 stars"
                >
                  ★★★★★
                </div>
                <p
                  style={{
                    fontFamily: futura,
                    fontSize: "15px",
                    lineHeight: 1.55,
                    color: "#222",
                    fontStyle: "italic",
                    margin: "0 0 14px",
                  }}
                >
                  &ldquo;{r.text}&rdquo;
                </p>
                <p
                  style={{
                    fontFamily: futura,
                    fontSize: "13px",
                    fontWeight: 700,
                    color: "#111",
                    margin: 0,
                  }}
                >
                  {r.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "30px" }}>
            College wallet questions, answered
          </h2>
          {COLLEGE_FAQ.map((item) => (
            <details
              key={item.q}
              style={{
                background: "#f7f7f7",
                borderRadius: "12px",
                border: "1px solid #e6e6e6",
                padding: "18px 22px",
                marginBottom: "12px",
                fontFamily: futura,
              }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: "15px",
                  color: "#111",
                }}
              >
                {item.q}
              </summary>
              <p
                style={{
                  margin: "10px 0 0",
                  fontSize: "15px",
                  lineHeight: 1.55,
                  color: "#555",
                }}
              >
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section
        style={{
          background: "#0c0c0c",
          color: "white",
          padding: "70px 24px",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "640px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, color: "white" }}>
            One wallet. ID, keys, cards, cash, and a tracker.
          </h2>
          <p
            style={{
              fontFamily: futura,
              fontSize: "16px",
              lineHeight: 1.6,
              color: "#cfcfcf",
              margin: "0 0 26px",
            }}
          >
            In stock now for $69.99. Processed in about 1 business day, arrives
            in 5-7 days. 30-day exchange guarantee.
          </p>
          <a href="/metal-wallet" style={ctaButton}>
            Buy the Metal Brik
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CollegeWallet;
